import React, { useEffect, useRef, useState, useCallback } from 'react';
import './AdvisorBox.scss';
import { useTranslation } from 'react-i18next';
import { useAccount } from '../../../contexts/account/AccountContexts';
import { B24_ADVISOR, PAID_ADVISOR, TRIAL_ADVISOR } from '../../../constans/constans';
import advisorAvatarTrial from '../../../../assets/images/advisor-avatar-trial.gif';
import advisorAvatarPaid from '../../../../assets/images/advisor-avatar-paid.gif';
import advisorAvatarB24 from '../../../../assets/images/advisor-avatar-b24.png';

const NARROW_MENU_BREAKPOINT = 1430;

const AdvisorBox: React.FC = () => {
  const { t } = useTranslation();
  const { accountResponse } = useAccount();
  const boxRef = useRef<HTMLDivElement>(null);
  const [popupPos, setPopupPos] = useState<{ top: number; left: number } | null>(null);

  // Klienci Brand24 (rejestracja z masterclass z `clientType: 'b24'`) mają własnego opiekuna —
  // wygrywa z podziałem trial/paid, bo przypisanie wynika z relacji handlowej, nie z planu.
  // `clientType` jest free-form, więc porównanie bez uwzględniania wielkości liter.
  const isB24Client = accountResponse?.clientType?.toLowerCase() === 'b24';
  // Sylwia tylko dla zwykłego triala. Support bywa ustawia status PAID na koncie z planem TRIAL —
  // to klient płacący, więc dostaje Ulę. Wystarczy jedno z dwóch: plan inny niż TRIAL albo status PAID.
  const isPaying =
    accountResponse?.accountType !== 'TRIAL' || accountResponse?.accountStatus === 'PAID';
  const advisor = isB24Client ? B24_ADVISOR : isPaying ? PAID_ADVISOR : TRIAL_ADVISOR;
  const advisorAvatar = isB24Client
    ? advisorAvatarB24
    : isPaying
      ? advisorAvatarPaid
      : advisorAvatarTrial;

  const hidePopup = useCallback(() => {
    setPopupPos(null);
  }, []);

  const togglePopup = useCallback(() => {
    if (!boxRef.current) return;
    // Popup zastępuje rozwinięty box tylko przy wąskim menu. Poza wąskim oknem menu bywa wąskie także
    // przez ręczne zwinięcie panelu (`.panel--small`) — sama szerokość okna już tego nie rozstrzyga.
    const isNarrowMenu =
      window.innerWidth <= NARROW_MENU_BREAKPOINT || Boolean(boxRef.current.closest('.panel--small'));
    if (!isNarrowMenu) return;

    const rect = boxRef.current.getBoundingClientRect();
    setPopupPos((current) => (current ? null : { top: rect.top, left: rect.right + 8 }));
  }, []);

  // Popup jest pozycjonowany `fixed` wobec miejsca, w którym stała karta, więc po przewinięciu
  // wisiałby w oderwaniu od niej — zamykamy go, jak inne menu w panelu. Kliknięcie poza zamyka tak
  // samo, bo otwarcie jest teraz świadome (klik), a nie przypadkowe (hover).
  useEffect(() => {
    if (!popupPos) {
      return;
    }

    const handleOutsideClick = (event: MouseEvent) => {
      if (!boxRef.current?.contains(event.target as Node)) {
        hidePopup();
      }
    };

    // `true` — łapiemy fazę przechwytywania, bo przewijana jest wewnętrzna kolumna panelu, a nie okno.
    document.addEventListener('scroll', hidePopup, true);
    document.addEventListener('mousedown', handleOutsideClick);

    return () => {
      document.removeEventListener('scroll', hidePopup, true);
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [popupPos, hidePopup]);

  return (
    <div className="advisor-box" ref={boxRef}>
      <div className="advisor-box__title">{t('advisor.title')}</div>
      {/* Klik obsługuje tylko karta w panelu — ta w popupie zamykałaby go przy próbie kliknięcia maila. */}
      <div className="advisor-box__card" onClick={togglePopup}>
        <img className="advisor-box__avatar" src={advisorAvatar} alt={advisor.name} />
        <div className="advisor-box__info">
          <span className="advisor-box__name">{advisor.name}</span>
          <a className="advisor-box__email" href={`mailto:${advisor.email}`}>
            {advisor.email}
          </a>
        </div>
      </div>
      <a
        href={advisor.calendly}
        target="_blank"
        rel="noopener noreferrer"
        className="advisor-box__demo-btn"
      >
        {t('bookADemo')}
      </a>
      {popupPos && (
        <div className="advisor-box__popup" style={{ top: popupPos.top, left: popupPos.left }}>
          <div className="advisor-box__card">
            <img className="advisor-box__avatar" src={advisorAvatar} alt={advisor.name} />
            <div className="advisor-box__info">
              <span className="advisor-box__name">{advisor.name}</span>
              <a className="advisor-box__email" href={`mailto:${advisor.email}`}>
                {advisor.email}
              </a>
            </div>
          </div>
          <a
            href={advisor.calendly}
            target="_blank"
            rel="noopener noreferrer"
            className="advisor-box__demo-btn"
          >
            {t('bookADemo')}
          </a>
        </div>
      )}
    </div>
  );
};

export default AdvisorBox;
